import React from "react";
import * as Apollo from "@apollo/client";
import { NodeEdge, NodeID } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	dateDiff,
	DateTime,
	dateTrunc,
	filterMap,
	formatDateTime,
	getTimeZoneName,
	isSameTime,
	isSameTimeOrAfter,
	stringToLocalDate,
	toGrammaticalNumber,
	TypeOrArray,
	useUpdateEffect,
} from "@hex-insights/core";
import {
	TimelineFilterInput,
	TimelineItem,
	TimelineObjectType,
	TimelineQuery,
	useTimelineAcademicTermListQuery,
	useTimelineAnnouncementListQuery,
	useTimelineCalendarEventListQuery,
	useTimelineContractListQuery,
	useTimelineNewsletterListQuery,
	useTimelinePaymentListQuery,
	useTimelinePostListQuery,
	useTimelineQuery,
	useTimelineSchoolAttendanceRecordListQuery,
	useTimelineStudentMedicalEventListQuery,
	useTimelineStudentNoteListQuery,
} from "../graphql-gen";

export type UseTimelineDataArgs = {
	filter?: TimelineFilterInput;
	pageSize?: number;
};

export function useTimelineData({ filter, pageSize = 25 }: UseTimelineDataArgs = {}) {
	const [lastOffset, setLastOffset] = React.useState(0);
	useUpdateEffect(() => {
		setLastOffset(0);
	}, [filter]);

	const { fetchMore, ...queryResult } = useTimelineQuery({
		variables: {
			filter,
			pagination: { limit: pageSize },
			timeZone: getTimeZoneName(),
		},
		notifyOnNetworkStatusChange: true,
		fetchPolicy: "cache-and-network",
	});

	const hasMore = queryResult.data?.timeline.hasMore ?? true;
	const loading = queryResult.loading;

	const loadMore = React.useCallback(() => {
		if (!hasMore || loading) {
			return;
		}
		const offset = lastOffset + pageSize;
		setLastOffset(offset);
		fetchMore({
			variables: {
				pagination: { limit: pageSize, offset },
			},
			updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
				...previousQueryResult,
				timeline: {
					...previousQueryResult.timeline,
					hasMore: fetchMoreResult.timeline.hasMore,
					items: [...previousQueryResult.timeline.items, ...fetchMoreResult.timeline.items],
				},
			}),
		});
	}, [hasMore, loading, pageSize, fetchMore, lastOffset]);

	const { loading: loadingAcademicTerms, objMap: academicTermsMap } = useAcademicTerms(queryResult.data);
	const { loading: loadingAnnouncements, objMap: announcementsMap } = useAnnouncements(queryResult.data);
	const { loading: loadingCalendarEvents, objMap: calendarEventsMap } = useCalendarEvents(queryResult.data);
	const { loading: loadingContracts, objMap: contractsMap } = useContracts(queryResult.data);
	const { loading: loadingNewsletters, objMap: newslettersMap } = useNewsletters(queryResult.data);
	const { loading: loadingPayments, objMap: paymentsMap } = usePayments(queryResult.data);
	const { loading: loadingPosts, objMap: postsMap } = usePosts(queryResult.data);
	const { loading: loadingSchoolAttendanceRecords, objMap: schoolAttendanceRecordsMap } = useSchoolAttendanceRecords(
		queryResult.data,
	);
	const { loading: loadingStudentMedicalEvents, objMap: studentMedicalEventsMap } = useStudentMedicalEvents(
		queryResult.data,
	);
	const { loading: loadingStudentNotes, objMap: studentNotesMap } = useStudentNotes(queryResult.data);

	return {
		...queryResult,
		loadMore,
		loadingAcademicTerms,
		academicTermsMap,
		loadingAnnouncements,
		announcementsMap,
		loadingCalendarEvents,
		calendarEventsMap,
		loadingContracts,
		contractsMap,
		loadingNewsletters,
		newslettersMap,
		loadingPayments,
		paymentsMap,
		loadingPosts,
		postsMap,
		loadingSchoolAttendanceRecords,
		schoolAttendanceRecordsMap,
		loadingStudentMedicalEvents,
		studentMedicalEventsMap,
		loadingStudentNotes,
		studentNotesMap,
	};
}

function useAcademicTerms(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.AcademicTerm,
		useTimelineAcademicTermListQuery,
		"academicTermConnection",
	);
}

function useAnnouncements(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.Announcement,
		useTimelineAnnouncementListQuery,
		"announcementConnection",
	);
}

function useCalendarEvents(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.CalendarEvent,
		useTimelineCalendarEventListQuery,
		"calendarEventConnection",
	);
}

function useContracts(data: TimelineQuery | undefined) {
	return useTimelineItemType(data, TimelineObjectType.Contract, useTimelineContractListQuery, "contractConnection");
}

function useNewsletters(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.Newsletter,
		useTimelineNewsletterListQuery,
		"newsletterConnection",
	);
}

function usePayments(data: TimelineQuery | undefined) {
	return useTimelineItemType(data, TimelineObjectType.Payment, useTimelinePaymentListQuery, "paymentConnection");
}

function usePosts(data: TimelineQuery | undefined) {
	return useTimelineItemType(data, TimelineObjectType.Post, useTimelinePostListQuery, "postConnection");
}

function useSchoolAttendanceRecords(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.SchoolAttendanceRecord,
		useTimelineSchoolAttendanceRecordListQuery,
		"schoolAttendanceRecordConnection",
	);
}

function useStudentMedicalEvents(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.StudentMedicalEvent,
		useTimelineStudentMedicalEventListQuery,
		"studentMedicalEventConnection",
	);
}

function useStudentNotes(data: TimelineQuery | undefined) {
	return useTimelineItemType(
		data,
		TimelineObjectType.StudentNote,
		useTimelineStudentNoteListQuery,
		"studentNoteConnection",
	);
}

type GenericObjectListQuery<K extends string, T extends { id: NodeID }> = {
	[key in K]: {
		edges: NodeEdge<T>[];
	};
};

type GenericObjectFilterInput = { filters?: TypeOrArray<{ idIn?: NodeID[] | null }> | null };

type UseItemTypeQuery<K extends string, T extends { id: NodeID }> = (
	options: Apollo.QueryHookOptions<GenericObjectListQuery<K, T>, GenericObjectFilterInput>,
) => Apollo.QueryResult<GenericObjectListQuery<K, T>, GenericObjectFilterInput>;

function useTimelineItemType<K extends string, T extends { id: NodeID }>(
	data: TimelineQuery | undefined,
	objectType: TimelineObjectType,
	useItemTypeQuery: UseItemTypeQuery<K, T>,
	key: K,
) {
	const [initialObjIDs, setInitialObjIDs] = React.useState(() => filterObjectIDs(data, objectType));
	const fetchedIDsRef = React.useRef<Set<NodeID>>(new Set(initialObjIDs));

	React.useEffect(() => {
		if (!data || initialObjIDs.length > 0) {
			return;
		}
		const objIDs = filterObjectIDs(data, objectType);
		if (objIDs.length === 0) {
			return;
		}
		setInitialObjIDs(objIDs);
		fetchedIDsRef.current = new Set(objIDs);
	}, [data, initialObjIDs, objectType]);

	const {
		loading,
		data: objData,
		fetchMore,
	} = useItemTypeQuery({
		variables: { filters: { idIn: initialObjIDs } },
		notifyOnNetworkStatusChange: true,
		skip: initialObjIDs.length === 0,
	});

	React.useEffect(() => {
		if (!data) {
			return;
		}
		const objIDs = filterObjectIDs(data, objectType);
		const newObjIDs = objIDs.filter((e) => !fetchedIDsRef.current.has(e));
		if (newObjIDs.length === 0) {
			return;
		}
		fetchMore({
			variables: { filters: { idIn: newObjIDs } },
			updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
				...previousQueryResult,
				...fetchMoreResult,
				[key]: {
					...previousQueryResult?.[key],
					...fetchMoreResult[key],
					edges: [...(previousQueryResult?.[key]?.edges ?? []), ...fetchMoreResult[key].edges],
				},
			}),
		});
		newObjIDs.forEach((e) => fetchedIDsRef.current.add(e));
	}, [data, objectType, fetchMore, key]);

	const objMap = React.useMemo(() => {
		if (!objData) {
			return {};
		}
		const objMap: Record<NodeID, T> = {};
		for (let i = 0; i < objData[key].edges.length; i++) {
			const obj = objData[key].edges[i].node;
			objMap[obj.id] = obj;
		}
		return objMap;
	}, [objData, key]);

	return { loading, objMap };
}

function filterObjectIDs(data: TimelineQuery | undefined, objectType: TimelineObjectType) {
	if (!data) {
		return [];
	}
	return filterMap(
		data?.timeline.items ?? [],
		(e) => e.objectType === objectType,
		(e) => e.objectID,
	);
}

export type TimelineItemGroup<T> = {
	heading: string | null;
	items: T[];
	groupType: "day" | "week" | "month" | null;
	groupStartTime: string;
};

export function useGroupedTimelineItems<T extends Pick<TimelineItem, "objectTime" | "objectTimeIsDate">>(items?: T[]) {
	const today = dateTrunc(new Date(), "day").toISOString();

	if (!items) {
		return [];
	}

	return groupTimelineItems(today, items);
}

export function groupTimelineItems<T extends Pick<TimelineItem, "objectTime" | "objectTimeIsDate">>(
	today: string,
	items: T[],
) {
	const itemGroups: TimelineItemGroup<T>[] = [];
	let currentGroup: TimelineItemGroup<T> = {
		heading: "Today",
		items: [],
		groupType: "day",
		groupStartTime: today,
	};

	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		const objectTime = item.objectTimeIsDate
			? dateTrunc(stringToLocalDate(item.objectTime, "day"), "day").toISOString()
			: item.objectTime;

		if (belongsInGroup(currentGroup, objectTime)) {
			currentGroup.items.push(item);
		} else {
			if (currentGroup.items.length > 0) {
				itemGroups.push(currentGroup);
			}
			const groupType = getGroupType(today, objectTime);
			const groupStartTime = getGroupStartTime(groupType, objectTime);
			currentGroup = {
				heading: getGroupHeading(today, groupType, groupStartTime, currentGroup.groupStartTime),
				items: [item],
				groupType: groupType,
				groupStartTime: groupStartTime,
			};
		}
	}
	if (currentGroup.items.length > 0) {
		itemGroups.push(currentGroup);
	}

	return itemGroups;
}

function getGroupType(today: string, objectTime: string) {
	if (dateDiff(dateTrunc(today, "day"), dateTrunc(objectTime, "day"), "day") < 2) {
		return "day";
	}
	const diffInMonths = dateDiff(dateTrunc(today, "month"), dateTrunc(objectTime, "month"), "months");
	if (diffInMonths === 0 || (diffInMonths === 1 && dateDiff(today, dateTrunc(today, "month")) < 15)) {
		return "week";
	}
	return "month";
}

function getGroupStartTime(groupType: Exclude<TimelineItemGroup<any>["groupType"], null>, objectTime: string) {
	return dateTrunc(objectTime, groupType).toISOString();
}

function getGroupHeading(
	today: string,
	groupType: TimelineItemGroup<any>["groupType"],
	groupStartTime: string,
	prevGroupStartTime: string,
) {
	if (groupType === "day" && isSameTime(today, groupStartTime, "day")) {
		return "Today";
	}
	if (groupType === "day" && isSameTime(addTimeToDate(today, [-1, "day"]), groupStartTime, "day")) {
		return "Yesterday";
	}
	if (groupType === "week") {
		const numWeeks = weekDiff(today, groupStartTime);
		if (numWeeks === 0) {
			return "This Week";
		}
		if (numWeeks === 1) {
			return "Last Week";
		}
		return `${toGrammaticalNumber("Week", numWeeks, true)} Ago`;
	}
	const diffInYears = dateDiff(dateTrunc(today, "year"), dateTrunc(groupStartTime, "year"), "years");
	if (
		diffInYears === 0 ||
		(diffInYears === 1 && dateDiff(today, dateTrunc(today, "year")) < 60) ||
		isSameTime(groupStartTime, prevGroupStartTime, "year")
	) {
		return formatDateTime(groupStartTime, "MMMM");
	}
	return formatDateTime(groupStartTime, "MMMM, YYYY");
}

function belongsInGroup(group: Pick<TimelineItemGroup<any>, "groupStartTime">, objectTime: string) {
	return isSameTimeOrAfter(objectTime, group.groupStartTime);
}

function weekDiff(end: DateTime, start: DateTime) {
	return dateDiff(dateTrunc(end, "week"), dateTrunc(start, "week"), "week");
}
